import { getTimeSlotsForDate, type TimeSlot, type TimeSlotDateOverride } from "./timeSlotUtils";
import { getZoneSlotsForDay, type ZoneWindow } from "../../supabase/functions/_shared/zoneSchedule";

export type { ZoneWindow } from "../../supabase/functions/_shared/zoneSchedule";
export { zoneHasOwnSchedule, getZoneSlotsForDay, mapZoneWindowRow } from "../../supabase/functions/_shared/zoneSchedule";
import { zoneHasOwnSchedule } from "../../supabase/functions/_shared/zoneSchedule";

/**
 * Créneaux effectifs d'une salle pour une date.
 * Salle sans horaires propres (ou pas de salle) → exactement getTimeSlotsForDate.
 */
export function getSlotsForZone(
  date: Date,
  zoneId: string | null | undefined,
  regularSlots: TimeSlot[],
  dateOverrides: TimeSlotDateOverride[],
  zoneWindows: ZoneWindow[],
  activeZoneIds?: Set<string>,
): TimeSlot[] {
  const own = getZoneSlotsForDay(date.getDay(), zoneId, zoneWindows, activeZoneIds);
  if (own === null) return getTimeSlotsForDate(date, regularSlots, dateOverrides);
  return own;
}

/**
 * Créneaux effectifs selon les salles choisies (préférence client multi-salles).
 * Miroir de resolveZoneSlotRows côté serveur : renvoie le calcul historique
 * sauf si TOUTES les salles choisies sont actives et ont des horaires propres ;
 * dans ce cas, l'union dédupliquée de leurs plages du jour.
 */
export function getSlotsForZonePreference(
  date: Date,
  zoneIds: string[],
  regularSlots: TimeSlot[],
  dateOverrides: TimeSlotDateOverride[],
  zoneWindows: ZoneWindow[],
  activeZoneIds?: Set<string>,
): TimeSlot[] {
  const fallback = () => getTimeSlotsForDate(date, regularSlots, dateOverrides);
  if (zoneIds.length === 0) return fallback();
  if (!zoneIds.every(z => zoneHasOwnSchedule(z, zoneWindows, activeZoneIds))) return fallback();
  const seen = new Set<string>();
  const slots: TimeSlot[] = [];
  for (const z of zoneIds) {
    for (const s of getZoneSlotsForDay(date.getDay(), z, zoneWindows, activeZoneIds) ?? []) {
      const key = `${s.start}-${s.end}`;
      if (seen.has(key)) continue;
      seen.add(key);
      slots.push(s);
    }
  }
  return slots.sort((a, b) => a.start.localeCompare(b.start));
}

/**
 * Créneaux proposés côté ÉQUIPE pour une date : services du restaurant
 * PLUS les plages des salles à horaires propres (union dédupliquée, triée).
 * Purement additif : aucune heure existante n'est retirée, donc un établissement
 * sans horaires de salle obtient exactement getTimeSlotsForDate.
 */
export function getStaffSlotsForDate(
  date: Date,
  regularSlots: TimeSlot[],
  dateOverrides: TimeSlotDateOverride[],
  zoneWindows: ZoneWindow[],
  activeZoneIds?: Set<string>,
): TimeSlot[] {
  const base = getTimeSlotsForDate(date, regularSlots, dateOverrides);
  if (!zoneWindows || zoneWindows.length === 0) return base;

  const day = date.getDay();
  const seen = new Set<string>();
  const result: TimeSlot[] = [];
  const push = (slot: TimeSlot) => {
    const key = `${slot.start.substring(0, 5)}-${slot.end.substring(0, 5)}`;
    if (seen.has(key)) return;
    seen.add(key);
    result.push(slot);
  };
  base.forEach(push);

  const zoneIds = Array.from(new Set(zoneWindows.filter(w => w.isActive).map(w => w.zoneId)));
  for (const zoneId of zoneIds) {
    for (const s of getZoneSlotsForDay(day, zoneId, zoneWindows, activeZoneIds) ?? []) {
      push({ start: s.start, end: s.end, availableDays: [day], durationOverrideMinutes: null });
    }
  }
  return result.sort((a, b) => a.start.localeCompare(b.start));
}

/**
 * Une table/salle est-elle ouverte pour une réservation donnée ?
 * Salle sans horaires propres (ou pas de salle) → true : comportement inchangé.
 * Sinon la réservation doit tenir entièrement dans une plage de la salle ce jour-là.
 */
export function isZoneOpenForReservation(
  zoneId: string | null | undefined,
  date: Date,
  startTime: string,
  durationMinutes: number,
  zoneWindows: ZoneWindow[],
  activeZoneIds?: Set<string>,
): boolean {
  const slots = getZoneSlotsForDay(date.getDay(), zoneId, zoneWindows, activeZoneIds);
  if (slots === null) return true;
  const toMin = (t: string) => {
    const [h, m] = t.substring(0, 5).split(':').map(Number);
    return h * 60 + m;
  };
  const start = toMin(startTime);
  const end = start + Math.max(0, durationMinutes);
  return slots.some(s => start >= toMin(s.start) && end <= toMin(s.end));
}
